/**
 * Utilities for limiting pasted tables to max 10 columns.
 * Used by TablePastePlugin and PasteImagePlugin.
 */

export const MAX_TABLE_COLUMNS = 10;

/**
 * Count columns in a table row (accounting for colspan).
 * @param {HTMLTableRowElement} tr
 * @returns {number}
 */
export function getRowColumnCount(tr) {
  let count = 0;
  const cells = tr.querySelectorAll(':scope > td, :scope > th');
  for (const cell of cells) {
    count += parseInt(cell.getAttribute('colspan') || '1', 10);
  }
  return count;
}

/**
 * Trim a table row to at most maxCols columns.
 * Removes cells from the end until the row has <= maxCols.
 * @param {HTMLTableRowElement} tr
 * @param {number} maxCols
 */
function trimRowToMaxColumns(tr, maxCols) {
  const cells = Array.from(tr.querySelectorAll(':scope > td, :scope > th'));
  let usedCols = 0;
  for (let i = 0; i < cells.length; i++) {
    const span = parseInt(cells[i].getAttribute('colspan') || '1', 10);
    if (usedCols + span > maxCols) {
      for (let j = i; j < cells.length; j++) {
        cells[j].remove();
      }
      break;
    }
    usedCols += span;
  }
}

/**
 * Trim tables in an HTML string to max 10 columns.
 * Use when loading existing content (edit mode) that may have been saved with >10 columns.
 * @param {string} htmlString - HTML content (e.g. from API or localStorage)
 * @returns {string} - HTML with tables trimmed to 10 columns
 */
export function trimTablesInHtml(htmlString) {
  if (!htmlString || typeof htmlString !== 'string') return htmlString;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    trimTablesToMaxColumns(doc);
    return doc.body ? doc.body.innerHTML : htmlString;
  } catch (err) {
    console.warn('[tablePasteUtils] trimTablesInHtml failed:', err);
    return htmlString;
  }
}

/**
 * Trim all tables in the document to max 10 columns.
 * @param {Document} doc - Parsed HTML document
 * @returns {number} - Number of tables that were trimmed (had > 10 cols)
 */
export function trimTablesToMaxColumns(doc) {
  let trimmedCount = 0;
  const tables = doc.querySelectorAll('table');
  for (const table of tables) {
    const firstRow = table.querySelector('tr');
    if (!firstRow) continue;
    const colCount = getRowColumnCount(firstRow);
    if (colCount > MAX_TABLE_COLUMNS) {
      const rows = table.querySelectorAll('tr');
      for (const tr of rows) {
        trimRowToMaxColumns(tr, MAX_TABLE_COLUMNS);
      }
      trimmedCount++;
    }
  }
  return trimmedCount;
}
